/**
 * Frontend mirror of services/risk/riskBands.js.
 * Keep the bands and helpers byte-for-byte in sync with the backend copy so
 * that UI labels always match server-persisted labels (both derive from the
 * same numeric score).
 */

export const RISK_BANDS = Object.freeze([
  { key: 'LOW', label: 'Low', min: 0, max: 29 },
  { key: 'MED', label: 'Medium', min: 30, max: 59 },
  { key: 'HIGH', label: 'High', min: 60, max: 79 },
  { key: 'CRITICAL', label: 'Critical', min: 80, max: 100 },
]);

export const RISK_BAND_LABELS = Object.freeze(['Low', 'Medium', 'High', 'Critical']);

export function clampScore(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(100, Math.round(n)));
}

export function deriveRiskLabel(score) {
  const s = clampScore(score);
  if (s == null) return null;
  for (const band of RISK_BANDS) {
    if (s >= band.min && s <= band.max) return band.label;
  }
  return null;
}

export function deriveRiskKey(score) {
  const s = clampScore(score);
  if (s == null) return null;
  for (const band of RISK_BANDS) {
    if (s >= band.min && s <= band.max) return band.key;
  }
  return null;
}

export function normalizeLabel(label) {
  if (label == null) return null;
  const s = String(label).trim().toUpperCase();
  if (s === 'CRITICAL') return 'Critical';
  if (s === 'HIGH') return 'High';
  if (s === 'MED' || s === 'MEDIUM' || s === 'MODERATE') return 'Medium';
  if (s === 'LOW') return 'Low';
  return null;
}

export function levelKey(label) {
  switch (normalizeLabel(label)) {
    case 'Critical':
      return 'CRITICAL';
    case 'High':
      return 'HIGH';
    case 'Medium':
      return 'MED';
    case 'Low':
      return 'LOW';
    default:
      return null;
  }
}

/**
 * Preferred front-end resolver: derive from score when finite, else fall back
 * to normalizing a provided label, else default to 'LOW'.
 */
export function resolveRiskKey({ score, level } = {}) {
  const fromScore = deriveRiskKey(score);
  if (fromScore) return fromScore;
  const fromLabel = levelKey(level);
  if (fromLabel) return fromLabel;
  return 'LOW';
}

export function resolveRiskLabel({ score, level } = {}) {
  const fromScore = deriveRiskLabel(score);
  if (fromScore) return fromScore;
  const fromLabel = normalizeLabel(level);
  if (fromLabel) return fromLabel;
  return 'Low';
}
